import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import PublicButton from '../components/PublicButton';
import {fetchMarketResources, selectMarketResources} from '../slices/marketSlice';
import {getResourceIcon} from '../helpers/iconHelper';
import ProgramBadges from '../components/ProgramBadges';
import MarketDetails from '../components/MarketDetails';

const REVEAL_KEY = 'marketsRevealed';
const COUNTUP_DURATION_MS = 700;

// Program documentation page for a resource, from the slug the API publishes.
const programUrl = (slug) => (slug ? `/docs/programs/${slug}/` : null);

function useReveal() {
  const [revealed, setRevealed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return sessionStorage.getItem(REVEAL_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    if (revealed) return;
    const id = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(id);
  }, [revealed]);
  useEffect(() => {
    if (revealed) {
      try { sessionStorage.setItem(REVEAL_KEY, '1'); } catch { /* noop */ }
    }
  }, [revealed]);
  return revealed;
}

function CountUp({value, format, duration = COUNTUP_DURATION_MS, start}) {
  const [n, setN] = useState(0);
  const nRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!start) return undefined;
    if (!Number.isFinite(value)) {
      nRef.current = value;
      setN(value);
      return undefined;
    }
    const prefersReduced = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      nRef.current = value;
      setN(value);
      return undefined;
    }
    const from = Number.isFinite(nRef.current) ? nRef.current : 0;
    const target = value;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = from + (target - from) * eased;
      nRef.current = next;
      setN(next);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        nRef.current = target;
        setN(target);
      }
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [start, value, duration]);

  return <>{format ? format(n) : Math.round(n)}</>;
}

function Stat({label, value, sub, className = '', revealClass, revealStyle, animate, format}) {
  return (
    <div className={`px-6 py-6 ${revealClass} ${className}`} style={revealStyle}>
      <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-gray-400">{label}</div>
      <div className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white font-mono tabular-nums">
        {Number.isFinite(value)
          ? <CountUp value={value} start={animate} format={format}/>
          : value}
      </div>
      {sub && <div className="mt-1 text-xs text-slate-500 dark:text-gray-400">{sub}</div>}
    </div>
  );
}

export default function Markets() {
  usePageTitle('Markets');
  const dispatch = useDispatch();
  const markets = useSelector(selectMarketResources);
  const mounted = useReveal();

  const headerCtaRef = useRef(null);
  const [headerCtaVisible, setHeaderCtaVisible] = useState(true);
  // Rows whose details panel is open. Independent per row, not persisted.
  const [openRows, setOpenRows] = useState(() => new Set());

  const toggleRow = (id) => setOpenRows((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  useEffect(() => {
    dispatch(fetchMarketResources());
  }, [dispatch]);

  useEffect(() => {
    const el = headerCtaRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeaderCtaVisible(entry.isIntersecting),
      {threshold: 0, rootMargin: '0px 0px -8px 0px'}
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const stats = useMemo(() => {
    const list = markets || [];
    const active = list.filter(m => !!m.is_active);
    const inactive = list.filter(m => !m.is_active);
    const totalBudget = active.reduce((s, r) => s + (parseFloat(r.budget) || 0), 0);
    const totalForecasters = parseInt(list[0]?.total_unique_forecasters, 10) || 0;
    const useCases = new Set(
      active.map(m => m.use_case || m.useCase).filter(Boolean)
    );
    return {
      total: list.length,
      activeCount: active.length,
      inactiveCount: inactive.length,
      totalBudget,
      totalForecasters,
      useCasesCount: useCases.size,
      useCasesLabel: [...useCases].map(uc => uc.replace(/_/g, ' ')).join(' / ') || '—',
    };
  }, [markets]);

  const sortedMarkets = useMemo(() => {
    return (markets || [])
      .filter(m => !!m.is_active)
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [markets]);

  const revealClass = `motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out ${
    mounted ? 'opacity-100 translate-y-0' : 'motion-safe:opacity-0 motion-safe:translate-y-2'
  }`;
  const revealStyle = (delayMs) => ({transitionDelay: `${delayMs}ms`});

  const formatBudget = (n) =>
    stats.totalBudget > 0
      ? `€${Math.round(n).toLocaleString(undefined, {maximumFractionDigits: 0})}`
      : '—';

  return (
    <div className="min-h-screen bg-surface dark:bg-gray-870 text-slate-900 dark:text-gray-100 flex flex-col">
      <PublicNav/>

      <section className="flex-1 max-w-7xl 2xl:max-w-[1600px] mx-auto px-6 pt-10 pb-20 w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
          <h1
            className={`text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] -tracking-[0.02em] text-slate-900 dark:text-white ${revealClass}`}
            style={revealStyle(0)}
          >
            Available <span className="italic font-semibold text-brand-600 dark:text-brand-400">markets</span>
          </h1>
          <PublicButton
            ref={headerCtaRef}
            to="/apply"
            variant="primary"
            className={`whitespace-nowrap shrink-0 ${revealClass}`}
            style={revealStyle(60)}
          >
            Apply to join →
          </PublicButton>
        </div>
        <p
          className={`mt-5 text-base sm:text-lg text-slate-700 dark:text-gray-300 ${revealClass}`}
          style={revealStyle(120)}
        >
          Explore our live forecasting markets. Each one offers a monthly prize pool and ranks your accuracy against peers, from independent forecasters to industrial players.
        </p>

        {/* Stats */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Active markets"
            value={stats.activeCount}
            sub={stats.inactiveCount > 0 ? `+${stats.inactiveCount} inactive` : 'all live'}
            animate={mounted}
            revealClass={revealClass}
            revealStyle={revealStyle(260)}
          />
          <Stat
            label="Monthly prize pool"
            value={stats.totalBudget}
            format={formatBudget}
            sub="across active markets"
            animate={mounted}
            revealClass={revealClass}
            revealStyle={revealStyle(340)}
            className="border-l border-slate-900/10 dark:border-gray-100/10"
          />
          <Stat
            label="Active forecasters"
            value={stats.totalForecasters}
            sub="submitted in last 30 days"
            animate={mounted}
            revealClass={revealClass}
            revealStyle={revealStyle(420)}
            className="lg:border-l lg:border-slate-900/10 lg:dark:border-gray-100/10"
          />
          <Stat
            label="Use cases"
            value={stats.useCasesCount}
            sub={stats.useCasesLabel}
            animate={mounted}
            revealClass={revealClass}
            revealStyle={revealStyle(500)}
            className="border-l border-slate-900/10 dark:border-gray-100/10"
          />
        </div>

        {/* Directory */}
        <div className="mt-16">
          <div
            className={`sticky top-[68px] sm:top-[72px] z-20 -mx-6 px-6 bg-surface/90 dark:bg-gray-870/90 backdrop-blur-md flex items-center justify-between gap-3 py-3 border-b border-slate-900/10 dark:border-gray-100/10 ${revealClass}`}
            style={revealStyle(580)}
          >
            <div className="flex items-baseline gap-2 text-xs uppercase tracking-wider text-slate-500 dark:text-gray-400">
              <span>Directory</span>
              <span aria-hidden="true" className="opacity-50">·</span>
              <span>{sortedMarkets.length} {sortedMarkets.length === 1 ? 'market' : 'markets'}</span>
              <span aria-hidden="true" className="opacity-50">·</span>
              <a href="/docs/programs/" className="normal-case text-brand-600 dark:text-brand-400 hover:underline underline-offset-4">programs guide</a>
            </div>
            <Link
              to="/apply"
              aria-hidden={headerCtaVisible}
              tabIndex={headerCtaVisible ? -1 : 0}
              className={`inline-flex items-center gap-1 text-xs font-medium text-slate-900 dark:text-white hover:underline underline-offset-4 whitespace-nowrap transition-opacity duration-200 ${
                headerCtaVisible ? 'opacity-0 pointer-events-none' : 'opacity-100'
              }`}
            >
              Apply to join →
            </Link>
          </div>

          <div className="divide-y divide-slate-900/10 dark:divide-gray-100/10 border-b border-slate-900/10 dark:border-gray-100/10">
            {sortedMarkets.length === 0 ? (
              <div className="py-6 text-sm italic text-slate-500">No markets yet.</div>
            ) : sortedMarkets.map((m, idx) => {
              const lastUpdated = m.updated_at || null;
              const budget = parseFloat(m.budget);
              const participants = parseInt(m.n_participants, 10);
              // The disclosure only exists once a market owner has published a
              // target sentence; an empty panel would read as a broken page.
              const hasDetails = !!m.profile?.published;
              const isOpen = hasDetails && openRows.has(m.id);
              const panelId = `market-details-${m.id}`;
              return (
                <article
                  key={m.id}
                  className={revealClass}
                  style={revealStyle(640 + idx * 90)}
                >
                  <div
                    className={`py-5 grid grid-cols-12 gap-x-4 gap-y-2 items-center ${
                      hasDetails ? 'cursor-pointer' : ''
                    }`}
                    onClick={hasDetails ? () => toggleRow(m.id) : undefined}
                  >
                    <div className="col-span-12 sm:col-span-4 flex items-center gap-4 min-w-0">
                      <div className="w-6 flex-shrink-0 text-slate-700 dark:text-gray-300">
                        {getResourceIcon(m.use_case || m.useCase || m.resource)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 dark:text-white truncate">{m.name}</div>
                        {m.program_summary?.horizons?.length ? (
                          <ProgramBadges summary={m.program_summary}/>
                        ) : (
                          <div className="text-xs text-slate-400 dark:text-gray-500 mt-1 truncate">
                            Schedule pending
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-span-4 sm:col-span-2">
                      <div className="text-sm font-mono text-slate-900 dark:text-white">
                        {Number.isFinite(budget) && budget > 0 ? `€${budget.toLocaleString()}` : '—'}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-gray-400">
                        monthly reward
                      </div>
                    </div>

                    <div className="col-span-4 sm:col-span-2">
                      <div className="text-sm font-mono text-slate-900 dark:text-white">
                        {Number.isFinite(participants) ? participants : '—'}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-gray-400">
                        active · 30d
                      </div>
                    </div>

                    <div className="col-span-4 sm:col-span-2">
                      <div className="text-sm text-slate-700 dark:text-gray-300">
                        {lastUpdated ? moment(lastUpdated).fromNow() : '—'}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-gray-400">
                        last update
                      </div>
                    </div>

                    <div className="col-span-12 sm:col-span-2 flex items-center gap-4 sm:justify-end">
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping"/>
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"/>
                        </span>
                        Live
                      </span>
                      {hasDetails && (
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                          onClick={(e) => { e.stopPropagation(); toggleRow(m.id); }}
                          className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-brand-600 hover:underline underline-offset-4 dark:text-brand-400"
                        >
                          {isOpen ? 'Hide' : 'Details'}
                          <svg
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                            className={`h-3 w-3 motion-safe:transition-transform motion-safe:duration-200 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          >
                            <path d="M5.2 7.2a1 1 0 0 1 1.4 0L10 10.6l3.4-3.4a1 1 0 1 1 1.4 1.4l-4.1 4.1a1 1 0 0 1-1.4 0L5.2 8.6a1 1 0 0 1 0-1.4z"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {hasDetails && (
                    <div
                      className={`grid motion-safe:transition-[grid-template-rows] motion-safe:duration-300 motion-safe:ease-out ${
                        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                      }`}
                    >
                      <div
                        id={panelId}
                        role="region"
                        aria-label={`${m.name} details`}
                        className="overflow-hidden"
                      >
                        <MarketDetails
                          resource={m}
                          programUrl={programUrl(m.profile?.program_slug)}
                        />
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>

      </section>

      <PublicFooter/>
    </div>
  );
}
